import { DeviceIds, deviceType } from '@/core/environment/deviceType';
import { useTracker } from '@/core/tracking';
import { usePageNameTracking } from '@/core/tracking/usePageNameTracking';
import { useRedeemGiftCode } from '@/features/giftCode/useRedeemGiftCode';
import { useResponsiveComparer } from '@/features/imageComparer/useResponsiveComparer';
import { useShowMobileUsersOnboarding } from '@/features/mobileUsers/useShowMobileUsersOnboarding';
import OnboardingModal from '@/features/onboarding/OnboardingModal';
import { useReminiaiFunnelRedirectState } from '@/features/reminiaiFunnel/useIsRedirectedFromReminiAi';
import { useLocalization } from '@/services/localizationService';
import { UserAction } from '@/services/userService';
import { isLoggedInSelector } from '@/stores/authStore';
import {
  cookieBannerResolvedBeforeSessionState,
  cookieBannerResolvedState,
  cookieLoginCtaShownState,
} from '@/stores/cookieLoginCtaStore';
import { modalState, pushModalSelector } from '@/stores/modalStore';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import settingsState from '@/stores/settingsStore';
import LinkWithQuery from '@/ui/content/LinkWithQuery';
import { resetAnimatedDivCount } from './animatedDiv/useAnimatedDiv';
import { resolveHomeUiLayout } from './homeUiLayout';

const filePickerId = 'home';
const cookieLoginCtaDelayMilliseconds = 500;

export const useHomeLayout = () => {
  const settings = useRecoilValue(settingsState);
  const hasResolvedCookieBanner = useRecoilValue(cookieBannerResolvedState);
  const wasCookieBannerResolvedBeforeSession = useRecoilValue(
    cookieBannerResolvedBeforeSessionState
  );
  const hasShownCookieLoginCta = useRecoilValue(cookieLoginCtaShownState);
  const isLoggedIn = useRecoilValue(isLoggedInSelector);
  const hasOpenModal = useRecoilValue(modalState).length > 0;
  const setHasShownCookieLoginCta = useSetRecoilState(cookieLoginCtaShownState);
  const pushModal = useSetRecoilState(pushModalSelector);
  const router = useRouter();
  const { track } = useTracker();
  const { t, data } = useLocalization();
  const homeUiLayout = resolveHomeUiLayout({
    remoteValue: settings.values.homeUiLayout,
    isReady: settings.areReady,
  });
  const { isRedirectedFromReminiAi, hasResolvedRedirect } =
    useReminiaiFunnelRedirectState();
  const isLeftCentered =
    homeUiLayout === 'left_centered' ||
    homeUiLayout === 'left_centered_flipped';
  /* Wait for the funnel check and exclude marketing-redirect visitors:
   * while the control renders, the mirrored hero must not load either. */
  const shouldRenderLeftCentered =
    isLeftCentered && hasResolvedRedirect && !isRedirectedFromReminiAi;
  const isFlippedVariant =
    homeUiLayout === 'left_centered_flipped' && shouldRenderLeftCentered;
  const comparerConfig = useResponsiveComparer(
    isFlippedVariant ? 'flipped' : 'default'
  );
  useRedeemGiftCode();
  usePageNameTracking('home');
  useShowMobileUsersOnboarding();

  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isCookieLoginCtaVisibleState, setIsCookieLoginCtaVisibleState] =
    useState(false);
  const lastEligibilityReason = useRef<string>();
  const isDesktop = useMemo(() => deviceType() === DeviceIds.Desktop, []);

  const displaySuppressionReason = useMemo(() => {
    if (!settings.areReady) return undefined;
    if (wasCookieBannerResolvedBeforeSession) {
      return 'existing_cookie_consent';
    }
    if (!hasResolvedCookieBanner) return undefined;
    if (!settings.values.shouldShowCookieLoginCta) return 'cta_disabled';
    if (!isDesktop) return 'not_desktop';
    if (isLoggedIn) return 'logged_in';
    if (hasOpenModal) return 'modal_open';
    return 'none';
  }, [
    hasOpenModal,
    hasResolvedCookieBanner,
    isDesktop,
    isLoggedIn,
    settings.areReady,
    settings.values.shouldShowCookieLoginCta,
    wasCookieBannerResolvedBeforeSession,
  ]);

  const shouldShowCookieLoginCta = displaySuppressionReason === 'none';
  const trackingSuppressionReason =
    displaySuppressionReason === 'none' &&
    hasShownCookieLoginCta &&
    !isCookieLoginCtaVisibleState
      ? 'already_shown'
      : displaySuppressionReason;

  useEffect(() => {
    if (
      trackingSuppressionReason === undefined ||
      lastEligibilityReason.current === trackingSuppressionReason
    ) {
      return;
    }

    lastEligibilityReason.current = trackingSuppressionReason;
    track('cookie_login_cta', 'eligibility_evaluated', {
      eligible: trackingSuppressionReason === 'none',
      suppressionReason: trackingSuppressionReason,
      page: 'home',
    });
  }, [track, trackingSuppressionReason]);

  useEffect(() => {
    if (!shouldShowCookieLoginCta) {
      setIsCookieLoginCtaVisibleState(false);
      return undefined;
    }

    if (hasShownCookieLoginCta) return undefined;

    const timeout = window.setTimeout(() => {
      setIsCookieLoginCtaVisibleState(true);
      setHasShownCookieLoginCta(true);
      track('cookie_login_cta', 'shown', { page: 'home' });
    }, cookieLoginCtaDelayMilliseconds);

    return () => window.clearTimeout(timeout);
  }, [
    hasShownCookieLoginCta,
    setHasShownCookieLoginCta,
    shouldShowCookieLoginCta,
    track,
  ]);

  const dismissCookieLoginCta = useCallback(() => {
    setIsCookieLoginCtaVisibleState(false);
    track('cookie_login_cta', 'dismissed', { page: 'home' });
  }, [track]);

  const openCookieLoginCtaLogin = useCallback(() => {
    setIsCookieLoginCtaVisibleState(false);
    track('cookie_login_cta', 'login_tapped', { page: 'home' });
    track('login', 'tapped', {
      actionValue: 'Log In / Sign Up (Cookie CTA)',
      page: 'home',
    });
    pushModal({
      id: 'user:onboarding',
      Modal: OnboardingModal,
      previousAction: UserAction.CookieLoginCta,
      loginOnly: true,
    });
  }, [pushModal, track]);

  const isCookieLoginCtaVisible =
    shouldShowCookieLoginCta && isCookieLoginCtaVisibleState;

  const onComparerSlide = useCallback(() => {
    if (isFirstSlide) {
      track('homepage', 'slider_interaction');
      setIsFirstSlide(false);
    }
  }, [track, isFirstSlide]);

  resetAnimatedDivCount();

  const onUpload = useCallback(
    (taskId: string) =>
      taskId &&
      router.replace(
        {
          pathname: `${router.pathname}result`,
          query: { ...router.query, taskId },
        },
        undefined,
        { shallow: true }
      ),
    [router]
  );

  const [animateHeader, setAnimateHeader] = useState<boolean>(false);
  const [hideFooter, setHideFooter] = useState<boolean>(false);
  const onScroll = useCallback(
    (shouldAnimate: boolean) => {
      setAnimateHeader(shouldAnimate);

      if (shouldAnimate && !hideFooter) {
        setHideFooter(true);
      }
    },
    [hideFooter]
  );

  const onPrivacyPolicyClick = useCallback((e) => e.stopPropagation(), []);

  return {
    filePickerId,
    comparerConfig,
    onComparerSlide,
    onUpload,
    onScroll,
    animateHeader,
    hideFooter,
    settings,
    homeUiLayout,
    shouldRenderLeftCentered,
    isFlippedVariant,
    isCookieLoginCtaVisible,
    dismissCookieLoginCta,
    openCookieLoginCtaLogin,
    titleText: t(data.home.title),
    subtitleText: t(data.home.subtitle),
    chooseFileText1: t(data.home.chooseFiles),
    chooseFileText2: t(data.home.chooseFile2),
    photosText: t(data.common.photos),
    videosText: t(data.common.videos),
    scrollToFooterText: t(data.footer.scrollToFooter),
    bipaText: t(data.common.bipaDisclaimer, {
      privacy: (text) => (
        <LinkWithQuery path="/privacy-and-cookie-policy">
          <a
            className="underline"
            target="_blank"
            onClick={onPrivacyPolicyClick}
          >
            {text}
          </a>
        </LinkWithQuery>
      ),
    }),
  };
};
